import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { useToasts } from '../../lib/toast';
import type { CategoryDto, ProductDto } from '@taj/types';

type Edit = { track: boolean; qty: string; thr: string; rq: string; ru: string; ppb: string };

export function StockManager({ onBack }: { onBack: () => void }) {
  const push = useToasts((s) => s.push);
  const [menu, setMenu] = useState<CategoryDto[]>([]);
  const [edits, setEdits] = useState<Record<string, Edit>>({});

  const load = () => {
    api.getMenu().then((m) => {
      setMenu(m);
      const e: Record<string, Edit> = {};
      m.forEach((c) => c.products.forEach((p) => {
        e[p.id] = { track: p.stock?.trackStock ?? false, qty: String(p.stock?.quantity ?? 0), thr: String(p.stock?.threshold ?? 5), rq: p.stock?.reorderQty ?? '', ru: p.stock?.reorderUnit ?? '', ppb: String(p.stock?.portionsPerBatch ?? 0) };
      }));
      setEdits(e);
    }).catch((err) => push(err.message, 'warn'));
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (id: string, patch: Partial<Edit>) => setEdits((s) => ({ ...s, [id]: { ...s[id], ...patch } }));

  const saveOne = async (p: ProductDto) => {
    const e = edits[p.id];
    try {
      await api.updateProduct(p.id, { trackStock: e.track, stockQty: parseInt(e.qty, 10) || 0, threshold: parseInt(e.thr, 10) || 0, reorderQty: e.rq.trim(), reorderUnit: e.ru.trim(), portionsPerBatch: parseInt(e.ppb, 10) || 0 });
      push(`✅ Stock enregistré · ${p.name}`);
      load();
    } catch (err: any) { push(err.message, 'warn'); }
  };

  // « J'ai reçu la livraison » → ajoute les portions du réappro au stock (ex. +40).
  const receive = async (p: ProductDto) => {
    const e = edits[p.id];
    const add = parseInt(e.ppb, 10) || 0;
    if (add <= 0) { push('Indique d’abord « = X portions » pour ce plat.', 'warn'); return; }
    const newQty = (parseInt(e.qty, 10) || 0) + add;
    try {
      await api.updateProduct(p.id, { trackStock: true, stockQty: newQty, threshold: parseInt(e.thr, 10) || 0, reorderQty: e.rq.trim(), reorderUnit: e.ru.trim(), portionsPerBatch: add });
      push(`📥 +${add} portions · ${p.name} (reste ${newQty})`);
      load();
    } catch (err: any) { push(err.message, 'warn'); }
  };

  return (
    <div className="staff-scroll">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '4px 0 12px' }}>
        <button className="km-back" onClick={onBack}>←</button>
        <div className="staff-title" style={{ fontSize: '1.4rem', flex: 1 }}>📦 Stock</div>
      </div>
      <div style={{ fontSize: '.82rem', color: 'var(--ink-faint)', marginBottom: 14, maxWidth: 620 }}>
        Active « Suivi », indique la <b>quantité en portions</b>, le seuil d'alerte, ce qu'il faut commander quand
        c'est bas (ex. 10 kg) et combien de <b>portions</b> ça donne (ex. 40). Le stock baisse à chaque vente ;
        sous le seuil → ajout auto à la 🛒 liste de courses ; à 0 → produit <b>masqué des bornes</b>. Quand tu reçois
        ta livraison, clique <b>📥 Reçu</b> pour ajouter les portions (ex. +40). 💾 pour enregistrer.
      </div>

      {menu.map((c) => (
        <div key={c.id} className="cc" style={{ marginBottom: 14 }}>
          <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: '1.15rem', marginBottom: 6 }}>{c.emoji} {c.name}</div>
          {c.products.length === 0 && <div style={{ fontSize: '.8rem', color: 'var(--ink-faint)' }}>Aucun plat.</div>}
          {c.products.map((p) => {
            const e = edits[p.id];
            if (!e) return null;
            const q = parseInt(e.qty, 10) || 0;
            const thr = parseInt(e.thr, 10) || 0;
            const color = !e.track ? 'var(--ink-faint)' : q <= 0 ? 'var(--cuisine)' : q <= thr ? 'var(--orange)' : 'var(--green)';
            const state = !e.track ? 'Non suivi' : q <= 0 ? 'Rupture' : q <= thr ? 'Stock bas' : 'OK';
            return (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 0', borderBottom: '1px solid var(--line)', flexWrap: 'wrap' }}>
                <span style={{ width: 10, height: 10, borderRadius: 5, background: color, flexShrink: 0 }} title={state} />
                <span style={{ fontSize: '1.2rem' }}>{p.emoji}</span>
                <span style={{ flex: '1 1 130px', fontWeight: 700, minWidth: 110 }}>{p.name}</span>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '.76rem', fontWeight: 700, color: 'var(--ink-dim)', whiteSpace: 'nowrap' }}>
                  <input type="checkbox" checked={e.track} onChange={(ev) => set(p.id, { track: ev.target.checked })} /> Suivi
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: '.64rem', color: 'var(--ink-faint)', fontWeight: 700 }}>Quantité
                  <input className="inp" inputMode="numeric" style={{ width: 70 }} disabled={!e.track} value={e.qty} onChange={(ev) => set(p.id, { qty: ev.target.value })} />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: '.64rem', color: 'var(--ink-faint)', fontWeight: 700 }}>Seuil alerte
                  <input className="inp" inputMode="numeric" style={{ width: 70 }} disabled={!e.track} value={e.thr} onChange={(ev) => set(p.id, { thr: ev.target.value })} />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: '.64rem', color: 'var(--ink-faint)', fontWeight: 700 }} title="Ajouté auto à la liste de courses quand le stock passe sous le seuil">🛒 À commander qd bas
                  <div style={{ display: 'flex', gap: 4 }}>
                    <input className="inp" style={{ width: 54 }} placeholder="Qté" disabled={!e.track} value={e.rq} onChange={(ev) => set(p.id, { rq: ev.target.value })} />
                    <input className="inp" style={{ width: 58 }} placeholder="Unité" disabled={!e.track} value={e.ru} onChange={(ev) => set(p.id, { ru: ev.target.value })} />
                  </div>
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: '.64rem', color: 'var(--ink-faint)', fontWeight: 700 }} title="Portions obtenues par 1 réappro (ex. 10 kg → 40 portions)">= portions
                  <input className="inp" inputMode="numeric" style={{ width: 60 }} disabled={!e.track} value={e.ppb} onChange={(ev) => set(p.id, { ppb: ev.target.value })} />
                </label>
                <button className="cr-btn" title={`J'ai reçu la livraison : +${parseInt(e.ppb, 10) || 0} portions`} disabled={!e.track} onClick={() => receive(p)} style={{ whiteSpace: 'nowrap' }}>📥 Reçu</button>
                <button className="cr-btn" title="Enregistrer" onClick={() => saveOne(p)}>💾</button>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
